import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { useApiClient } from '@/hooks/useApiClient';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '@/components/LanguageSelector';

type Step = 1 | 2 | 3;
type Phase = 'PROMPT' | 'FORM' | 'SUCCESS';
type EquipmentType = 'FRIDGE' | 'FREEZER' | 'FRYER' | 'OTHER';
type MemberRole = 'STAFF' | 'AUDITOR';

const SECTOR_SUGGESTIONS = [
  'Réfrigération',
  'Congélation',
  'Cuisson',
  'Stockage sec',
];

const TEMP_DEFAULTS: Record<EquipmentType, { min: string; max: string }> = {
  FRIDGE: { min: '0', max: '4' },
  FREEZER: { min: '-25', max: '-18' },
  FRYER: { min: '0', max: '10' },
  OTHER: { min: '0', max: '10' },
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>(1);
  const [phase, setPhase] = useState<Phase>('PROMPT');
  const [lastCreated, setLastCreated] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [eqName, setEqName] = useState('');
  const [eqType, setEqType] = useState<EquipmentType>('FRIDGE');
  const [eqMinTemp, setEqMinTemp] = useState(TEMP_DEFAULTS.FRIDGE.min);
  const [eqMaxTemp, setEqMaxTemp] = useState(TEMP_DEFAULTS.FRIDGE.max);
  const [eqSectorName, setEqSectorName] = useState(SECTOR_SUGGESTIONS[0]);

  const [prepName, setPrepName] = useState('');
  const [prepCategory, setPrepCategory] = useState('');
  const [prepDays, setPrepDays] = useState('');
  const [prepHours, setPrepHours] = useState('');

  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState<MemberRole>('STAFF');

  const CATEGORY_CHIPS = [
    t('onboarding.categories.sauces'),
    t('onboarding.categories.meats'),
    t('onboarding.categories.fish'),
    t('onboarding.categories.desserts'),
    t('onboarding.categories.general'),
  ];

  const STEP_TITLES: Record<Step, string> = {
    1: t('onboarding.step1Title'),
    2: t('onboarding.step2Title'),
    3: t('onboarding.step3Title'),
  };

  const handleTypeChange = (type: EquipmentType) => {
    setEqType(type);
    setEqMinTemp(TEMP_DEFAULTS[type].min);
    setEqMaxTemp(TEMP_DEFAULTS[type].max);
  };

  const goToStep = (s: Step) => {
    setStep(s);
    setPhase('PROMPT');
    setLastCreated(null);
    setError(null);
  };

  const complete = () => {
    if (user?.id) localStorage.setItem(`onboarding_seen_${user.id}`, 'true');
    navigate('/', { replace: true });
  };

  const skipStep = () => {
    if (step < 3) goToStep((step + 1) as Step);
    else complete();
  };

  const equipmentMutation = useMutation({
    mutationFn: async () => {
      if (!eqName.trim()) throw new Error(t('onboarding.nameRequired'));
      if (eqType === 'FRYER') return apiClient.createOilStation(eqName.trim());

      const trimmedSector = eqSectorName.trim();
      if (!trimmedSector) throw new Error('Veuillez sélectionner un secteur');
      const sector = await apiClient.findOrCreateEquipmentSector(trimmedSector);
      return apiClient.createEquipment(eqName.trim(), eqType, Number(eqMinTemp), Number(eqMaxTemp), sector.id);
    },
    onSuccess: () => {
      setLastCreated(eqName.trim());
      setEqName(''); setEqType('FRIDGE');
      setEqMinTemp(TEMP_DEFAULTS.FRIDGE.min); setEqMaxTemp(TEMP_DEFAULTS.FRIDGE.max);
      setError(null); setPhase('SUCCESS');
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: ['oil-stations'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-sectors'] });
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || err?.message || t('onboarding.equipmentError'));
    },
  });

  const prepMutation = useMutation({
    mutationFn: async () => {
      if (!prepName.trim()) throw new Error(t('onboarding.nameRequired'));
      return apiClient.createInternalPreparation({
        name: prepName.trim(),
        category: prepCategory.trim() || t('onboarding.categories.general'),
        shelfLifeDays: Number(prepDays) || 0,
        shelfLifeHours: Number(prepHours) || 0,
      });
    },
    onSuccess: () => {
      setLastCreated(prepName.trim());
      setPrepName(''); setPrepCategory(''); setPrepDays(''); setPrepHours('');
      setError(null); setPhase('SUCCESS');
      queryClient.invalidateQueries({ queryKey: ['dlc-preparations'] });
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || err?.message || t('onboarding.prepError'));
    },
  });

  const memberMutation = useMutation({
    mutationFn: async () => {
      if (!memberEmail.trim()) throw new Error(t('onboarding.emailRequired'));
      if (!user?.establishmentId) throw new Error(t('onboarding.establishmentNotConfigured'));
      return apiClient.addTeamMember(user.establishmentId, { email: memberEmail.trim(), role: memberRole });
    },
    onSuccess: () => {
      setLastCreated(memberEmail.trim());
      setMemberEmail(''); setMemberRole('STAFF');
      setError(null); setPhase('SUCCESS');
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || err?.message || t('onboarding.memberError'));
    },
  });

  const isPending = equipmentMutation.isPending || prepMutation.isPending || memberMutation.isPending;

  const handleCreate = () => {
    setError(null);
    if (step === 1) equipmentMutation.mutate();
    else if (step === 2) prepMutation.mutate();
    else memberMutation.mutate();
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      {/* Header */}
      <div className="bg-[#1B365D] text-white py-4 px-6 flex items-center justify-between">
        <span className="font-bold text-base tracking-wide">{STEP_TITLES[step]}</span>
        <LanguageSelector />
      </div>

      {/* Step progress dots */}
      <div className="flex justify-center gap-2 py-4 bg-white border-b border-neutral-100">
        {([1, 2, 3] as Step[]).map((s) => (
          <div
            key={s}
            className={`h-2 w-8 rounded-full transition-colors ${
              s === step ? 'bg-teal-600' : s < step ? 'bg-teal-300' : 'bg-neutral-200'
            }`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 flex justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">

          {/* PROMPT phase */}
          {phase === 'PROMPT' && (
            <div className="flex flex-col items-center text-center gap-6">
              <div className={`w-full h-48 rounded-2xl flex items-center justify-center text-8xl ${step === 1 ? 'bg-teal-50' : step === 2 ? 'bg-sky-50' : 'bg-green-50'}`}>
                {step === 1 ? '❄️' : step === 2 ? '📦' : '👥'}
              </div>

              <div>
                <h2 className="text-2xl font-extrabold text-[#1B365D] mb-3">
                  {step === 1 && t('onboarding.step1Heading')}
                  {step === 2 && t('onboarding.step2Heading')}
                  {step === 3 && t('onboarding.step3Heading')}
                </h2>
                <p className="text-neutral-500 leading-relaxed">
                  {step === 1 && t('onboarding.step1Description')}
                  {step === 2 && t('onboarding.step2Description')}
                  {step === 3 && t('onboarding.step3Description')}
                </p>
              </div>

              {step === 3 && !user?.establishmentId ? (
                <div className="w-full bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                  {t('onboarding.noEstablishment')}
                </div>
              ) : (
                <button
                  onClick={() => setPhase('FORM')}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-3.5 rounded-xl transition-colors"
                >
                  {step === 1 && t('onboarding.createEquipment')}
                  {step === 2 && t('onboarding.createPrep')}
                  {step === 3 && t('onboarding.inviteMember')}
                </button>
              )}

              <button
                onClick={skipStep}
                className="text-neutral-500 hover:text-neutral-700 text-sm font-semibold py-2 transition-colors"
              >
                {step === 3 ? t('onboarding.finishTutorial') : t('onboarding.skipStep')}
              </button>
            </div>
          )}

          {/* FORM phase */}
          {phase === 'FORM' && (
            <div className="flex flex-col gap-5">
              <div className="bg-white rounded-2xl p-5 border-l-4 border-teal-600 shadow-sm">

                {/* Step 1 — Equipment */}
                {step === 1 && (
                  <>
                    <label className="block text-sm font-semibold text-neutral-700 mb-1.5">
                      {t('onboarding.equipmentName')}
                    </label>
                    <input
                      type="text"
                      value={eqName}
                      onChange={(e) => setEqName(e.target.value)}
                      placeholder={t('onboarding.equipmentNamePlaceholder')}
                      autoFocus
                      className="w-full border border-neutral-200 rounded-lg px-3.5 py-2.5 text-sm bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-teal-500 mb-4"
                    />

                    <label className="block text-sm font-semibold text-neutral-700 mb-1.5">{t('onboarding.type')}</label>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(['FRIDGE', 'FREEZER', 'FRYER', 'OTHER'] as EquipmentType[]).map((type) => (
                        <button
                          key={type}
                          onClick={() => handleTypeChange(type)}
                          className={`px-3 py-1.5 rounded-full text-sm font-semibold border-2 transition-colors ${
                            eqType === type
                              ? 'border-teal-600 bg-teal-50 text-teal-700'
                              : 'border-neutral-200 bg-white text-neutral-500 hover:border-neutral-300'
                          }`}
                        >
                          {type === 'FRIDGE' ? t('onboarding.fridge')
                            : type === 'FREEZER' ? t('onboarding.freezer')
                            : type === 'FRYER' ? t('onboarding.fryer')
                            : t('onboarding.other')}
                        </button>
                      ))}
                    </div>

                    {eqType !== 'FRYER' && (
                      <>
                        <label className="block text-sm font-semibold text-neutral-700 mb-1.5 mt-4">
                          Secteur <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {SECTOR_SUGGESTIONS.map((name) => (
                            <button
                              key={name}
                              type="button"
                              onClick={() => setEqSectorName(name)}
                              className={`px-3 py-1.5 rounded-full text-sm font-semibold border-2 transition-colors ${
                                eqSectorName === name
                                  ? 'border-teal-600 bg-teal-50 text-teal-700'
                                  : 'border-neutral-200 bg-white text-neutral-500 hover:border-neutral-300'
                              }`}
                            >
                              {name}
                            </button>
                          ))}
                        </div>
                        <input
                          type="text"
                          value={eqSectorName}
                          onChange={(e) => setEqSectorName(e.target.value)}
                          placeholder="Ou saisir un secteur personnalisé..."
                          className="w-full border border-neutral-200 rounded-lg px-3.5 py-2.5 text-sm bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-teal-500 mb-4"
                        />

                        <label className="block text-sm font-semibold text-neutral-700 mb-1.5">
                          {t('onboarding.temperatureRange')}
                        </label>
                        <div className="flex gap-3">
                          <div className="flex-1">
                            <span className="text-xs text-neutral-400 mb-1 block">{t('onboarding.min')}</span>
                            <input type="number" value={eqMinTemp} onChange={(e) => setEqMinTemp(e.target.value)} className="w-full border border-neutral-200 rounded-lg px-3 py-2.5 text-sm bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-teal-500" />
                          </div>
                          <div className="flex-1">
                            <span className="text-xs text-neutral-400 mb-1 block">{t('onboarding.max')}</span>
                            <input type="number" value={eqMaxTemp} onChange={(e) => setEqMaxTemp(e.target.value)} className="w-full border border-neutral-200 rounded-lg px-3 py-2.5 text-sm bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-teal-500" />
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Step 2 — DLC Preparation */}
                {step === 2 && (
                  <>
                    <label className="block text-sm font-semibold text-neutral-700 mb-1.5">
                      {t('onboarding.prepName')}
                    </label>
                    <input
                      type="text"
                      value={prepName}
                      onChange={(e) => setPrepName(e.target.value)}
                      placeholder={t('onboarding.prepNamePlaceholder')}
                      autoFocus
                      className="w-full border border-neutral-200 rounded-lg px-3.5 py-2.5 text-sm bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-teal-500 mb-4"
                    />

                    <label className="block text-sm font-semibold text-neutral-700 mb-1.5">{t('onboarding.category')}</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {CATEGORY_CHIPS.map((chip) => (
                        <button
                          key={chip}
                          onClick={() => setPrepCategory(prepCategory === chip ? '' : chip)}
                          className={`px-3 py-1.5 rounded-full text-sm font-semibold border-2 transition-colors ${
                            prepCategory === chip
                              ? 'border-teal-600 bg-teal-50 text-teal-700'
                              : 'border-neutral-200 bg-white text-neutral-500 hover:border-neutral-300'
                          }`}
                        >
                          {chip}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={prepCategory}
                      onChange={(e) => setPrepCategory(e.target.value)}
                      placeholder={t('onboarding.customCategory')}
                      className="w-full border border-neutral-200 rounded-lg px-3.5 py-2.5 text-sm bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-teal-500 mb-4"
                    />

                    <label className="block text-sm font-semibold text-neutral-700 mb-1.5">
                      {t('onboarding.shelfLife')}
                    </label>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <span className="text-xs text-neutral-400 mb-1 block">{t('onboarding.days')}</span>
                        <input type="number" value={prepDays} onChange={(e) => setPrepDays(e.target.value)} placeholder="0" className="w-full border border-neutral-200 rounded-lg px-3 py-2.5 text-sm bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-teal-500" />
                      </div>
                      <div className="flex-1">
                        <span className="text-xs text-neutral-400 mb-1 block">{t('onboarding.hours')}</span>
                        <input type="number" value={prepHours} onChange={(e) => setPrepHours(e.target.value)} placeholder="0" className="w-full border border-neutral-200 rounded-lg px-3 py-2.5 text-sm bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-teal-500" />
                      </div>
                    </div>
                  </>
                )}

                {/* Step 3 — Team member */}
                {step === 3 && (
                  <>
                    <label className="block text-sm font-semibold text-neutral-700 mb-1.5">
                      {t('onboarding.emailAddress')}
                    </label>
                    <input
                      type="email"
                      value={memberEmail}
                      onChange={(e) => setMemberEmail(e.target.value)}
                      placeholder={t('onboarding.emailPlaceholder')}
                      autoFocus
                      autoComplete="off"
                      className="w-full border border-neutral-200 rounded-lg px-3.5 py-2.5 text-sm bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-teal-500 mb-4"
                    />

                    <label className="block text-sm font-semibold text-neutral-700 mb-1.5">{t('onboarding.role')}</label>
                    <div className="flex gap-3">
                      {(['STAFF', 'AUDITOR'] as MemberRole[]).map((r) => (
                        <button
                          key={r}
                          onClick={() => setMemberRole(r)}
                          className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-colors ${
                            memberRole === r
                              ? 'border-teal-600 bg-teal-50 text-teal-700'
                              : 'border-neutral-200 bg-white text-neutral-500 hover:border-neutral-300'
                          }`}
                        >
                          {r === 'STAFF' ? t('onboarding.staff') : t('onboarding.auditor')}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {error && (
                  <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {error}
                  </div>
                )}

                <div className="flex gap-3 mt-5">
                  <button
                    onClick={() => { setError(null); setPhase('PROMPT'); }}
                    className="px-4 py-2.5 border border-neutral-200 rounded-lg text-sm font-semibold text-neutral-600 hover:bg-neutral-50 transition-colors"
                  >
                    {t('onboarding.cancel')}
                  </button>
                  <button
                    onClick={handleCreate}
                    disabled={isPending}
                    className="flex-1 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white font-bold py-2.5 rounded-lg text-sm transition-colors"
                  >
                    {isPending ? t('onboarding.creating') : t('onboarding.create')}
                  </button>
                </div>
              </div>

              <button
                onClick={skipStep}
                className="text-neutral-500 hover:text-neutral-700 text-sm font-semibold py-2 text-center transition-colors"
              >
                {step === 3 ? t('onboarding.finishTutorial') : t('onboarding.skipStep')}
              </button>
            </div>
          )}

          {/* SUCCESS phase */}
          {phase === 'SUCCESS' && (
            <div className="flex flex-col items-center gap-4">
              <div className="w-full bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
                <div className="text-4xl mb-3">✅</div>
                <p className="font-semibold text-green-800">
                  {step === 3
                    ? t('onboarding.invitedMember', { email: lastCreated })
                    : t('onboarding.createdItem', { name: lastCreated })}
                </p>
              </div>

              <button
                onClick={() => { setLastCreated(null); setPhase('FORM'); }}
                className="w-full bg-teal-50 hover:bg-teal-100 border border-teal-200 text-teal-700 font-semibold py-3 rounded-xl text-sm transition-colors"
              >
                {step === 1 ? t('onboarding.createAnotherEquipment')
                  : step === 2 ? t('onboarding.createAnotherPrep')
                  : t('onboarding.inviteAnotherMember')}
              </button>

              {step < 3 ? (
                <button
                  onClick={() => goToStep((step + 1) as Step)}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-3.5 rounded-xl transition-colors"
                >
                  {t('onboarding.continueToStep', { step: step + 1 })}
                </button>
              ) : (
                <button
                  onClick={complete}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-3.5 rounded-xl transition-colors"
                >
                  {t('onboarding.finishTutorial')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
